import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, BookOpen, FileText, Download, Filter, ChevronDown, SlidersHorizontal, ArrowLeft, CheckCircle, Loader2, AlertTriangle, LayoutDashboard } from 'lucide-react';
import TetfundLogo from '../../assets/images/TetfundLogo.svg';
import { libraryService, LibrarySubmission } from '../../services/libraryService';
import { authorAuthService, Category } from '../../features/author/services/authorAuth';
import { FILE_BASE_URL } from '../../api/axios';
import { useAuth } from '../hooks/auth';

// --- Types ---

type ResourceType = 'Book' | 'Journal';


export default function ResourceLibrary() {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState<ResourceType>('Book');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [isbnSearch, setIsbnSearch] = useState('');
    const [keywordSearch, setKeywordSearch] = useState('');

    const [resources, setResources] = useState<LibrarySubmission[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { isAuthenticated, user } = useAuth();
    const location = useLocation();

    const isAuthorizedView = location.pathname.startsWith('/author') || location.pathname.startsWith('/admin');

    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

    // Fetch Categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await authorAuthService.getAllCategories(true);
                if (response.isSuccess && response.data) {
                    setCategories(response.data);
                }
            } catch (err) {
                }
        };
        fetchCategories();
    }, []);

    // Fetch Resources
    useEffect(() => {
        const fetchResources = async () => {
            setLoading(true);
            setError(null);
            try {
                const applicationType = selectedType === 'Book' ? 1 : 2;
                const categoryId = selectedCategory ? Number(selectedCategory) : 0;

                // Construct search query
                let query = searchTerm;
                if (isbnSearch) query += (query ? ' ' : '') + isbnSearch;
                if (keywordSearch) query += (query ? ' ' : '') + keywordSearch;

                const response = await libraryService.getApprovedSubmissions({
                    pageNumber: 1, // API usually expects 1-based or 0-based, assuming 1 for now
                    pageSize: 100, // Load a reasonable chunk
                    sortBy: 'submittedAt',
                    sortDescending: true,
                    search: query,
                    categoryId: categoryId,
                    applicationType: applicationType
                });

                if (response.isSuccess) {
                    setResources(response.data.data || []);
                } else {
                    setError(response.message || 'Failed to fetch resources');
                    setResources([]);
                }
            } catch (err) {
                setError('An unexpected error occurred while fetching resources.');
                setResources([]);
            } finally {
                setLoading(false);
            }
        };

        const timeoutId = setTimeout(fetchResources, 500); // Debounce
        return () => clearTimeout(timeoutId);
    }, [searchTerm, selectedType, selectedCategory, isbnSearch, keywordSearch]);

    const filteredResources = resources; // API handles filtering now


    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans">

            {/* Header - Only show on public route */}
            {!isAuthorizedView && (
                <header className="bg-white border-b border-gray-200 sticky top-0 z-[60] shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Link to="/" className="flex items-center gap-2">
                                <img src={TetfundLogo} alt="TETFund Logo" className="h-10 w-auto bg-green-50 p-1 rounded shadow-sm" />
                                <div className="hidden md:block border-l border-gray-300 pl-3 ml-1">
                                    <span className="text-xl font-bold text-gray-900 tracking-tight">TAG Resource Library</span>
                                </div>
                            </Link>
                        </div>

                        <div className="flex items-center gap-4">
                            {isAuthenticated ? (
                                <Link
                                    to={user?.role === 'author' ? '/author' : '/admin'}
                                    className="flex items-center gap-2 bg-[#088A61] text-white px-5 py-2.5 rounded-lg text-sm font-bold shadow-md hover:bg-[#067451] transition-all transform hover:scale-105 active:scale-95"
                                >
                                    <LayoutDashboard size={18} />
                                    Back to Dashboard
                                </Link>
                            ) : (
                                <Link
                                    to="/login"
                                    className="bg-[#088A61] text-white px-6 py-2.5 rounded-lg text-sm font-bold shadow-md hover:bg-[#067451] transition-all transform hover:scale-105 active:scale-95"
                                >
                                    Log in to Portal
                                </Link>
                            )}
                        </div>
                    </div>
                </header>
            )}

            {/* Hero Search Section */}
            <div className="bg-[#088A61] relative overflow-hidden">
                {/* Background Pattern logic */}
                <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] mix-blend-overlay"></div>
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white opacity-5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-black opacity-10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 text-center">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
                        Discover Academic Excellence
                    </h1>
                    <p className="text-lg md:text-xl text-green-50 mb-10 max-w-2xl mx-auto font-light">
                        Browse and download a comprehensive collection of approved books and journals submitted by Nigerian scholars.
                    </p>

                    <div className="relative max-w-2xl mx-auto shadow-2xl rounded-2xl bg-white flex items-center p-2 focus-within:ring-4 focus-within:ring-green-400/30 transition-all">
                        <Search className="w-6 h-6 text-gray-400 ml-3 shrink-0" />
                        <input
                            type="text"
                            placeholder="Search by title, author, or abstract..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-transparent border-none focus:ring-0 text-gray-900 text-lg py-3 px-4 outline-none placeholder-gray-400 font-medium"
                        />
                        {/* <button className="bg-gray-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-black transition-colors shrink-0 hidden sm:block">
                            Search
                        </button> */}
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-4 xl:px-8 py-10 w-full flex-grow flex flex-col md:flex-row gap-5 lg:gap-3 xl:gap-6 items-start">

                {/* Mobile Filters Toggle */}
                <button
                    onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
                    className="md:hidden w-full bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between font-semibold text-gray-700 shadow-sm"
                >
                    <div className="flex items-center gap-2">
                        <Filter size={18} />
                        Filters & Sorting
                    </div>
                    <ChevronDown className={`transition-transform ${isMobileFiltersOpen ? 'rotate-180' : ''}`} size={20} />
                </button>

                {/* Sidebar Filters */}
                <aside className={`w-full md:w-60 lg:w-56 xl:w-64 shrink-0 bg-white border border-gray-200 rounded-xl p-6 shadow-sm ${!isMobileFiltersOpen ? 'hidden md:block' : 'block'}`}>
                    <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
                        <SlidersHorizontal size={20} className="text-gray-900" />
                        <h2 className="text-lg font-bold text-gray-900">Filter Results</h2>
                    </div>

                    {/* Resource Type Component */}
                    <div className="mb-8">
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Resource Type</h3>
                        <div className="space-y-2">
                            {(['Book', 'Journal'] as const).map(type => (
                                <button
                                    key={type}
                                    onClick={() => setSelectedType(type)}
                                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group outline-none"
                                >
                                    <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${selectedType === type ? 'bg-[#088A61] border-[#088A61]' : 'border-gray-300 bg-white group-hover:border-gray-400'}`}>
                                        {selectedType === type && <CheckCircle size={14} className="text-white" />}
                                    </div>
                                    <span className={`text-sm font-medium ${selectedType === type ? 'text-gray-900' : 'text-gray-600'}`}>{type}s</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Thematic Area */}
                    <div className="mb-8">
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Thematic Area</h3>
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent block p-3 outline-none font-medium appearance-none"
                        >
                            <option value="">All Thematic Areas</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Specific Search Fields */}
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Keywords</h3>
                            <input
                                type="text"
                                placeholder="e.g. Health, Solar..."
                                value={keywordSearch}
                                onChange={(e) => setKeywordSearch(e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg focus:ring-2 focus:ring-[#088A61] outline-none block p-3"
                            />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">
                                {selectedType === 'Book' ? 'ISBN No.' : 'ISSN No.'}
                            </h3>
                            <input
                                type="text"
                                placeholder={selectedType === 'Book' ? "Enter ISBN No..." : "Enter ISSN No..."}
                                value={isbnSearch}
                                onChange={(e) => setIsbnSearch(e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg focus:ring-2 focus:ring-[#088A61] outline-none block p-3 font-mono"
                            />
                        </div>
                    </div>

                    {/* Reset button */}
                    <div className="mt-8 pt-6 border-t border-gray-100">
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setSelectedType('Book');
                                setSelectedCategory('');
                                setIsbnSearch('');
                                setKeywordSearch('');
                            }}
                            className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-lg transition-colors"
                        >
                            Clear All Filters
                        </button>
                    </div>
                </aside>

                {/* Main Content Grid */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                            {loading ? 'Searching...' : `${filteredResources.length} ${filteredResources.length === 1 ? 'Resource' : 'Resources'}`}
                        </h2>
                        <div className="text-sm text-gray-500 hidden sm:block">
                            Showing results for {selectedType.toLowerCase()}s
                        </div>
                    </div>

                    {loading ? (
                        <div className="bg-white rounded-2xl border border-gray-200 p-24 text-center flex flex-col items-center justify-center shadow-sm">
                            <Loader2 className="w-12 h-12 text-[#088A61] animate-spin mb-4" />
                            <h3 className="text-lg font-bold text-gray-900">Searching Library...</h3>
                            <p className="text-gray-500">Please wait while we find the best resources for you.</p>
                        </div>
                    ) : filteredResources.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                <Search className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">No resources found</h3>
                            <p className="text-gray-500 max-w-sm">We couldn't find anything matching your current filters. Try adjusting your search criteria or clearing filters.</p>
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('');
                                    setIsbnSearch('');
                                    setKeywordSearch('');
                                }}
                                className="mt-6 text-[#088A61] font-bold hover:underline"
                            >
                                Clear all filters
                            </button>
                        </div>
                    ) : error ? (
                        <div className="bg-white rounded-2xl border border-red-100 p-16 text-center flex flex-col items-center justify-center shadow-sm">
                            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-4">
                                <AlertTriangle className="w-8 h-8 text-red-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
                            <p className="text-gray-500 max-w-sm">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="mt-6 bg-[#088A61] text-white px-6 py-2 rounded-lg font-bold hover:bg-[#067451] transition-all"
                            >
                                Retry
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 xl:gap-6">
                            {filteredResources.map(resource => (
                                <div key={resource.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 group flex flex-col h-full transform hover:-translate-y-1">

                                    {/* Card Header area */}
                                    <div className="p-5 flex flex-col gap-4 border-b border-gray-100">
                                        <div className="flex flex-col min-w-0 flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded flex items-center gap-1 w-fit
                                                    ${selectedType === 'Book' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'bg-purple-50 text-purple-700 border border-purple-100'}`}
                                                >
                                                    {selectedType === 'Book' ? <BookOpen size={10} /> : <FileText size={10} />}
                                                    {selectedType}
                                                </span>
                                                <span className="text-[10px] uppercase font-bold text-gray-400 border border-gray-200 rounded px-2 py-1">
                                                    {new Date(resource.submittedAt).getFullYear()}
                                                </span>
                                            </div>
                                            <h3 className="font-bold text-gray-900 text-lg leading-snug line-clamp-2 md:line-clamp-3 mb-1 group-hover:text-[#088A61] transition-colors">{resource.title}</h3>
                                            <p className="text-sm font-medium text-gray-600 line-clamp-1">{resource.authors}</p>
                                        </div>
                                    </div>

                                    {/* Card Body */}
                                    <div className="p-5 flex-1 flex flex-col justify-between bg-[#FAFAFA]/50">
                                        <div>
                                            <p className="text-sm text-gray-600 line-clamp-3 mb-4 leading-relaxed font-light">{resource.abstract}</p>

                                            <div className="flex flex-wrap gap-1.5 mb-4">
                                                {(resource.category?.name ? [resource.category.name] : []).map(kw => ( // Using category as a keyword for now
                                                    <span key={kw} className="bg-green-50 text-[#088A61] text-[10px] px-2 py-1 rounded-full font-semibold border border-[#088A61]/10">
                                                        {kw}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                                            <span className="text-xs font-semibold text-gray-500">
                                                {selectedType === 'Book' ? 'ISBN: ' : 'ISSN: '}
                                                <span className="text-gray-900">{resource.isbnNo || 'N/A'}</span>
                                            </span>

                                            <button
                                                onClick={() => resource.manuscriptUrl && window.open(`${FILE_BASE_URL}${resource.manuscriptUrl}`, '_blank')}
                                                className="flex items-center gap-1.5 text-sm font-bold text-[#088A61] hover:text-[#067451] bg-[#088A61]/10 hover:bg-[#088A61]/20 px-3 py-1.5 rounded-lg transition-colors"
                                            >
                                                <Download size={14} />
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>

            {/* Simple Footer */}
            <footer className="mt-auto bg-white border-t border-gray-200 py-8 text-center text-sm text-gray-500">
                <p>© {new Date().getFullYear()} TETFund TAG. All rights reserved.</p>
                <p className="mt-1">Providing accessible academic resources to scholars nationwide.</p>
            </footer>
        </div>
    );
}
